import { useState, useEffect } from 'react';
import { useWallet } from '../../context/WalletContext';
import { useDolphin } from '../../hooks/useDolphin';
import { useAutoRegisterReferrer } from '../../hooks/useAutoRegister';
import { getLevelsSafe } from '../../data/levelsData';
import Overview from '../admin/Overview';
import AdminAnalytics from '../admin/AdminAnalytics';
import MiningAndTokenomics from '../admin/MiningAndTokenomics';
import TreasuryAndSecurity from '../admin/TreasuryAndSecurity';
import AdminUserActivity from '../admin/AdminUserActivity';
import Wallet from './Wallet';
import LevelsTab from './LevelsTab';
import Mine from './Mine';
import Claim from './Claim';
import UserAnalytics from './UserAnalytics';
import { toast } from 'react-toastify';
import { ethers } from 'ethers';
import { 
  FaCoins,
  FaShieldAlt,
  FaArrowUp, 
  FaDollarSign, 
 
  FaGem,
 
  FaHammer,
  FaGift,
  FaCalculator
} from 'react-icons/fa';
import Referral from './Referral';


// Helper to safely convert BigNumber-like objects to number
const toNumberSafe = (val) => {
  if (val == null) return 0;
  if (typeof val === 'object' && val._isBigNumber && typeof val.toString === 'function') {
    return Number(val.toString());
  }
  return Number(val);
};

const Dashboard = ({ activeTab = 'overview', setActiveTab }) => {
  // Wallet and admin tab bar (HOOKS MUST BE AT TOP LEVEL)
    // Automatically set referrer for all users if not set (permanent logic)
    useAutoRegisterReferrer({ defaultInviteCode: null });
  const { isConnected, account, isAdmin } = useWallet();
  const { stats, userStats, txLoading, mineTokens, claimTokens, setUserReferrer, referralStats } = useDolphin();
  
  // Use local state as fallback if props not provided
  const [localActiveTab, setLocalActiveTab] = useState('overview');
  const currentTab = setActiveTab ? activeTab : localActiveTab;
  const handleSetTab = setActiveTab || setLocalActiveTab;

  // Log DOLP price info for debugging
  useEffect(() => {
    if (stats) {
      console.log('[DOLP Price] Current:', toNumberSafe(stats.currentPrice));
      // console.log('[DOLP Price] End:', toNumberSafe(stats.endPrice));
    }
  }, [stats]);



  // Show referral modal after connect if not set
  useEffect(() => {
    if (isConnected && account && currentTab === 'overview') {
        // Check for referral code in localStorage
      // Removed unused variable: refParam
      const localReferral = localStorage.getItem('inviteCode') || localStorage.getItem('referrer');
      // Only show for new users: no levels and no referrer
      const hasNoLevels = !userStats || !userStats.levels || userStats.levels.length === 0;
      if (hasNoLevels && !localReferral) {
        // Removed references to setInitialInviteCode and setShowReferralModal
      }
    }
  }, [isConnected, account, userStats, currentTab]);

    const [mineAmount, setMineAmount] = useState('');
  
  // Removed unused variables: initialInviteCode, showReferralModal, registering, registerError, handleReferralSubmit

  // Enhanced the ConnectPrompt modal for wallet selection
  if (!isConnected && localStorage.getItem('walletPromptSkipped') !== 'true') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-gradient-to-br from-gray-950 to-gray-900 text-white p-6 rounded-xl shadow-xl max-w-md w-full">
          <h2 className="text-2xl font-bold mb-4">Connect Your Wallet</h2>
          <p className="text-gray-400 mb-6">To access the dashboard, please connect your wallet.</p>
          <div className="flex gap-4">
            <button
              onClick={() => {/* Logic to trigger wallet connection */}}
              className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:from-cyan-700 hover:to-blue-700"
            >
              Connect Wallet
            </button>
            <button
              onClick={() => localStorage.setItem('walletPromptSkipped', 'true')}
              className="bg-gray-700 text-gray-300 px-4 py-2 rounded-lg font-semibold hover:bg-gray-600"
            >
              Skip
            </button>
          </div>
        </div>
      </div>
    );
  }


  // Guard against undefined stats or totalSupply
  const miningProgress = (
    stats && stats.totalSupply && stats.minedTokens &&
    (!isNaN(toNumberSafe(stats.totalSupply)) && toNumberSafe(stats.totalSupply) > 0)
  )
    ? (toNumberSafe(stats.minedTokens) / toNumberSafe(stats.totalSupply)) * 100
    : 0;

  // Removed priceProgress calculation using startPrice

  // Use claimable amount from userStats (synced with contract getClaimableAmount)
  const claimableAmount = userStats && userStats.claimable ? toNumberSafe(userStats.claimable) : 0;

  // Mining handlers
  const handleAmountChange = (e) => {
    setMineAmount(e.target.value);
  };

  const handleMine = async () => {
    if (!mineAmount || parseFloat(mineAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    // If user has an invite code in localStorage and no referrer set, set it on-chain before mining
    // Only attempt to set referrer if an invite code exists
    if ((!referralStats?.referrer || referralStats.referrer === '0x0000000000000000000000000000000000000000') && localStorage.getItem('inviteCode')) {
      try {
        await setUserReferrer(localStorage.getItem('inviteCode'));
        toast.success('Referrer set successfully!');
      } catch (err) {
        toast.error('Failed to set referrer: ' + (err.message || 'Unknown error'));
        // If setting referrer fails, allow user to proceed with purchase
      }
    }
    try {
      // USDT mining only, use ethers.js for correct conversion
      const usdtAmount = ethers.utils.parseUnits(mineAmount, 6).toString();
      const result = await mineTokens(usdtAmount);
      if (!result || !result.tx) {
        toast.error('Transaction was not triggered. Please ensure your wallet is authenticated and try again.');
        return;
      }
      toast.success(`Successfully mined ${mineAmount && !isNaN(mineAmount) && parseFloat(mineAmount) > 0 ? parseFloat(mineAmount).toFixed(4) : '0.0000'} DOLP tokens!`);
      setMineAmount('');
    } catch (err) {
      if (err.message && err.message.match(/reentrancy|already processing|duplicate|locked|nonReentrant/i)) {
        toast.error('A transaction is already processing. Please wait for the previous transaction to complete.');
      } else if (err.message && err.message.includes('Insufficient balance')) {
        toast.error('Insufficient balance to complete this purchase. Please check your balance.');
      } else if (err.message && err.message.includes('Contract not connected')) {
        toast.error('Contract not connected. Please refresh and reconnect your wallet.');
      } else if (err.message && err.message.includes('Wallet not connected')) {
        toast.error('Wallet not connected. Please reconnect your wallet.');
      } else {
        toast.error(err.message || 'Failed to mine tokens');
      }
    }
  };

  const handleClaim = async () => {
    try {
      await claimTokens();
      toast.success('Tokens claimed successfully!');
    } catch (err) {
      if (err.message && err.message.match(/reentrancy|already processing|duplicate|locked|nonReentrant/i)) {
        toast.error('A transaction is already processing. Please wait for the previous transaction to complete.');
      } else {
        toast.error(err.message || 'Failed to claim tokens');
      }
    }
  };

  // If wallet or history tab, render the full-page component
  if (currentTab === 'wallet') {
    return <Wallet />;
  }
  if (currentTab === 'history') {
    return <Wallet initialSection="history" />;
  }
  
  const tabSectionClass = "bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 min-h-screen p-4 rounded-2xl shadow-xl border border-cyan-800/30";
  const cardClass = "bg-gray-800/40 rounded-xl p-4 border border-gray-700 flex flex-col items-start sm:bg-gray-100/80 sm:dark:bg-gray-800/60 sm:rounded sm:p-2 sm:border sm:border-gray-200 sm:dark:border-gray-800 xs:p-1 xs:text-xs";

  if (currentTab === 'referral') {
    return (
      <div className={tabSectionClass}>
        <Referral />
      </div>
    );
  }

  if (currentTab === 'levels') {
    return <LevelsTab />;
  }

  if (currentTab === 'mine') {
    return <Mine />;
  }

  if (currentTab === 'claim') {
    return <Claim />;
  }

  if (currentTab === 'user-analytics') {
    return (
      <div className={tabSectionClass}>
        <UserAnalytics />
      </div>
    );
  }

  // Updated the overview tab with theme colors
  if (currentTab === 'overview') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white overflow-x-hidden w-full">
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-sm p-4 border border-gray-700 shadow-xl overflow-hidden w-full sm:bg-white/80 sm:dark:bg-gray-900/80 sm:rounded-md sm:p-2 sm:border sm:border-gray-200 sm:dark:border-gray-800 sm:shadow-xs xs:p-1 xs:text-xs">
          <div className="flex gap-3 mb-2 sm:gap-2 sm:mb-1 xs:gap-1 xs:mb-0.5">
            <div className="p-2.5 bg-cyan-500/20 rounded-lg sm:p-1.5 sm:rounded-md xs:p-1 xs:rounded">
              <FaDollarSign className="w-5 h-5 text-cyan-400 sm:w-4 sm:h-4 xs:w-3 xs:h-3" />
            </div>
            <div>
              <h3 className="text-lg font-semibold sm:text-base xs:text-xs">Current Token Price</h3>
              <p className="text-3xl font-bold text-cyan-400 mt-2 sm:text-xl sm:mt-1 xs:text-base xs:mt-0.5">
                {stats && stats.currentPrice !== undefined ? `$${toNumberSafe(stats.currentPrice).toFixed(2)}` : '-'}
              </p>
            </div>
          </div>
        </div>
        {/* Holdings Grid - Responsive, smaller on mobile */}
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-4 border border-gray-700 shadow-xl w-full mt-2 sm:bg-white/80 sm:dark:bg-gray-900/80 sm:rounded-md sm:p-2 sm:border sm:border-gray-200 sm:dark:border-gray-800 sm:shadow-xs xs:p-1">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 w-full sm:gap-2 xs:gap-1">
                        {/* DOLP Rewards/Lifetime Stats */}
                        <div className="bg-gray-800/40 rounded-xl p-4 border border-yellow-700 flex flex-col items-start sm:bg-yellow-100/80 sm:dark:bg-yellow-800/60 sm:rounded sm:p-2 sm:border sm:border-yellow-200 sm:dark:border-yellow-800 xs:p-1 xs:text-xs col-span-2 lg:col-span-1">
                          <div className="flex items-center gap-3 mb-2 sm:gap-2 sm:mb-1 xs:gap-1 xs:mb-0.5">
                            <div className="p-2 bg-yellow-500/20 rounded-lg sm:p-1 sm:rounded xs:p-0.5 xs:rounded">
                              <FaGift className="w-4 h-4 text-yellow-400 sm:w-3 sm:h-3 xs:w-2.5 xs:h-2.5" />
                            </div>
                            <span className="text-sm text-yellow-400 font-bold sm:text-xs xs:text-[10px]">DOLP Rewards</span>
                          </div>
                          <div className="text-xs text-gray-300 mb-1">Lifetime Earned: <span className="font-bold text-yellow-300">{userStats && userStats.vested !== undefined ? `${toNumberSafe(userStats.vested).toLocaleString()} DOLP` : '-'}</span></div>
                          <div className="text-xs text-gray-300 mb-1">Claimed: <span className="font-bold text-green-300">{userStats && userStats.totalClaimed !== undefined ? `${toNumberSafe(userStats.totalClaimed).toLocaleString()} DOLP` : '-'}</span></div>
                          <div className="text-xs text-gray-300 mb-1">Claimable: <span className="font-bold text-cyan-300">{userStats && userStats.claimable !== undefined ? `${toNumberSafe(userStats.claimable).toLocaleString()} DOLP` : '-'}</span></div>
                          <div className="text-xs text-gray-300">Pending: <span className="font-bold text-orange-300">{userStats && userStats.pending !== undefined ? `${toNumberSafe(userStats.pending).toLocaleString()} DOLP` : '-'}</span></div>
                        </div>
            {/* Vested Tokens */}
            <div className="bg-gray-800/40 rounded-xl p-4 border border-gray-700 flex flex-col items-start sm:bg-gray-100/80 sm:dark:bg-gray-800/60 sm:rounded sm:p-2 sm:border sm:border-gray-200 sm:dark:border-gray-800 xs:p-1 xs:text-xs">
              <div className="flex items-center gap-3 mb-2 sm:gap-2 sm:mb-1 xs:gap-1 xs:mb-0.5">
                <div className="p-2 bg-emerald-500/20 rounded-lg sm:p-1 sm:rounded xs:p-0.5 xs:rounded">
                  <FaGem className="w-4 h-4 text-emerald-400 sm:w-3 sm:h-3 xs:w-2.5 xs:h-2.5" />
                </div>
                <span className="text-sm text-gray-400 sm:text-xs sm:text-gray-500 xs:text-[10px] xs:text-gray-500">Vested Tokens</span>
              </div>
              <p className="text-xl font-bold text-emerald-400 sm:text-base xs:text-xs">
                {userStats && userStats.vested !== undefined 
                  ? `${toNumberSafe(userStats.vested).toLocaleString()} DOLP`
                  : '-'
                }
              </p>
            </div>
            {/* Claimable Now */}
            <div className="bg-gray-800/40 rounded-xl p-4 border border-gray-700 flex flex-col items-start sm:bg-gray-100/80 sm:dark:bg-gray-800/60 sm:rounded sm:p-2 sm:border sm:border-gray-200 sm:dark:border-gray-800 xs:p-1 xs:text-xs">
              <div className="flex items-center gap-3 mb-2 sm:gap-2 sm:mb-1 xs:gap-1 xs:mb-0.5">
                <div className="p-2 bg-cyan-500/20 rounded-lg sm:p-1 sm:rounded xs:p-0.5 xs:rounded">
                  <FaCoins className="w-4 h-4 text-cyan-400 sm:w-3 sm:h-3 xs:w-2.5 xs:h-2.5" />
                </div>
                <span className="text-sm text-gray-400 sm:text-xs sm:text-gray-500 xs:text-[10px] xs:text-gray-500">Claimable Now</span>
              </div>
              <p className="text-xl font-bold text-cyan-400 sm:text-base xs:text-xs">
                {stats && stats.miningEnded 
                  ? `${claimableAmount.toLocaleString(undefined, {maximumFractionDigits: 2})} DOLP`
                  : 'After mining ends'
                }
              </p>
            </div>
            {/* Your Level */}
            <div className="bg-gray-800/40 rounded-xl p-4 border border-gray-700 flex flex-col items-start sm:bg-gray-100/80 sm:dark:bg-gray-800/60 sm:rounded sm:p-2 sm:border sm:border-gray-200 sm:dark:border-gray-800 xs:p-1 xs:text-xs">
              <div className="flex items-center gap-3 mb-2 sm:gap-2 sm:mb-1 xs:gap-1 xs:mb-0.5">
                <div className="p-2 bg-purple-500/20 rounded-lg sm:p-1 sm:rounded xs:p-0.5 xs:rounded">
                  <FaHammer className="w-4 h-4 text-purple-400 sm:w-3 sm:h-3 xs:w-2.5 xs:h-2.5" />
                </div>
                <span className="text-sm text-gray-400 sm:text-xs sm:text-gray-500 xs:text-[10px] xs:text-gray-500">Your Level</span>
              </div>
              <p className="text-xl font-bold text-purple-400 sm:text-base xs:text-xs">
                {getLevelsSafe(userStats).length > 0
                  ? `Level ${Math.max(...getLevelsSafe(userStats).map(toNumberSafe))}`
                  : 'No level unlocked'
                }
              </p>
            </div>
            {/* Next Level */}
            <div className="bg-gray-800/40 rounded-xl p-4 border border-gray-700 flex flex-col items-start sm:bg-gray-100/80 sm:dark:bg-gray-800/60 sm:rounded sm:p-2 sm:border sm:border-gray-200 sm:dark:border-gray-800 xs:p-1 xs:text-xs">
              <div className="flex items-center gap-3 mb-2 sm:gap-2 sm:mb-1 xs:gap-1 xs:mb-0.5">
                <div className="p-2 bg-emerald-500/20 rounded-lg sm:p-1 sm:rounded xs:p-0.5 xs:rounded">
                  <FaArrowUp className="w-4 h-4 text-emerald-400 sm:w-3 sm:h-3 xs:w-2.5 xs:h-2.5" />
                </div>
                <span className="text-sm text-gray-400 sm:text-xs sm:text-gray-500 xs:text-[10px] xs:text-gray-500">Next Level</span>
              </div>
              <p className="text-xl font-bold text-emerald-400 sm:text-base xs:text-xs">
                {getLevelsSafe(userStats).length > 0 && Math.max(...getLevelsSafe(userStats).map(toNumberSafe)) < 12
                  ? `Level ${Math.max(...getLevelsSafe(userStats).map(toNumberSafe)) + 1}`
                  : getLevelsSafe(userStats).length > 0 ? 'Max level' : 'Level 1'
                }
              </p>
            </div>
            {/* MLM Eligibility */}
            <div className="bg-gray-800/40 rounded-xl p-4 border border-gray-700 flex flex-col items-start sm:bg-gray-100/80 sm:dark:bg-gray-800/60 sm:rounded sm:p-2 sm:border sm:border-gray-200 sm:dark:border-gray-800 xs:p-1 xs:text-xs">
              <div className="flex items-center gap-3 mb-2 sm:gap-2 sm:mb-1 xs:gap-1 xs:mb-0.5">
                <div className="p-2 bg-purple-500/20 rounded-lg sm:p-1 sm:rounded xs:p-0.5 xs:rounded">
                  <FaGift className="w-4 h-4 text-purple-400 sm:w-3 sm:h-3 xs:w-2.5 xs:h-2.5" />
                </div>
                <span className="text-sm text-gray-400 sm:text-xs sm:text-gray-500 xs:text-[10px] xs:text-gray-500">Referral Earnings</span>
              </div>
              <p className="text-xl font-bold text-purple-400 sm:text-base xs:text-xs">
                {getLevelsSafe(userStats).length > 0
                  ? `Up to Level ${Math.max(...getLevelsSafe(userStats).map(toNumberSafe))}`
                  : 'Unlock level'
                }
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render admin section if owner and sidebar admin tab is active
  if (isAdmin && currentTab && currentTab.startsWith('admin-')) {
    return (
      <div className="dashboard-container fade-in">
        <div className="dashboard-header">
          <div className="welcome-section">
            <h1 className="dashboard-title">Admin Dashboard</h1>
            <p className="dashboard-subtitle">
              <FaShieldAlt style={{ marginRight: '0.5rem' }} />
              {account ? `${account.slice(0, 6)}...${account.slice(-4)}` : ''}
            </p>
          </div>
        </div>
        <div className="admin-tab-content">
          {currentTab === 'admin-overview' && <Overview />}
          {currentTab === 'admin-mining-tokenomics' && <MiningAndTokenomics />}
          {currentTab === 'admin-treasury-security' && <TreasuryAndSecurity />}
          {currentTab === 'admin-analytics' && <AdminAnalytics />}
          {currentTab === 'admin-user-activity' && <AdminUserActivity />}
        </div>
      </div>
    );
  }

  // Add contract analytics and payout stats section
  // Removed unused variable: analytics

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-gray-100 dark:from-gray-950 dark:to-gray-900 text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-800 text-base">
      {/* Mobile Header */}
      <div className="lg:hidden bg-gradient-to-br from-white to-gray-100 dark:from-gray-950 dark:to-gray-900 backdrop-blur-sm border-b border-gray-200 dark:border-gray-800 p-2 sm:p-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Removed Dolphin Logo */}
            <div>
              <h1 className="text-lg font-bold">
                {currentTab === 'overview' && 'Dashboard'}
                {currentTab === 'mine' && 'Mine Tokens'}
                {currentTab === 'levels' && 'Packages'}
                {currentTab === 'claim' && 'Claim Tokens'}
                {currentTab === 'user-analytics' && 'User Analytics'}
              </h1>
              {/* Removed wallet address repetition */}
            </div>
          </div>
          <div className="flex gap-2">
            {currentTab === 'overview' && (
              <>
                <button 
                  className="p-2 bg-cyan-600 rounded-lg"
                  onClick={() => handleSetTab('mine')}
                >
                  <FaHammer className="w-4 h-4" />
                </button>
                <button 
                  className="p-2 bg-emerald-600 rounded-lg"
                  onClick={() => handleSetTab('claim')}

                >
                  <FaGift className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden lg:block p-4 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-br from-white to-gray-100 dark:from-gray-950 dark:to-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Removed Dolphin Logo */}
              <div>
                <h1 className="text-2xl font-bold">
                  {currentTab === 'overview' && 'Dashboard Overview'}
                  {currentTab === 'mine' && 'Mine Tokens'}
                  {currentTab === 'levels' && 'Educational Packages'}
                  {currentTab === 'claim' && 'Claim Tokens'}
                  {currentTab === 'user-analytics' && 'User Analytics'}
                </h1>
                {/* Removed wallet address repetition */}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-0 w-full max-w-full">
        {currentTab === 'overview' && (
         <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white overflow-x-hidden w-full">
            {/* Price Card - Responsive, smaller on mobile */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-sm p-4 border border-gray-700 shadow-xl overflow-hidden w-full sm:bg-white/80 sm:dark:bg-gray-900/80 sm:rounded-md sm:p-2 sm:border sm:border-gray-200 sm:dark:border-gray-800 sm:shadow-xs xs:p-1 xs:text-xs">
              <div className="flex gap-3 mb-2 sm:gap-2 sm:mb-1 xs:gap-1 xs:mb-0.5">
                <div className="p-2.5 bg-cyan-500/20 rounded-lg sm:p-1.5 sm:rounded-md xs:p-1 xs:rounded">
                  <FaDollarSign className="w-5 h-5 text-cyan-400 sm:w-4 sm:h-4 xs:w-3 xs:h-3" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold sm:text-base xs:text-xs">Current Token Price</h3>
                  <p className="text-3xl font-bold text-cyan-400 mt-2 sm:text-xl sm:mt-1 xs:text-base xs:mt-0.5">
                    {stats && stats.currentPrice !== undefined ? `$${toNumberSafe(stats.currentPrice).toFixed(2)}` : '-'}
                  </p>
                </div>
              </div>
              {/* Removed Start Price display for cleaner UI */}
            </div>
            {/* Holdings Grid - Responsive, smaller on mobile */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-4 border border-gray-700 shadow-xl w-full mt-2 sm:bg-white/80 sm:dark:bg-gray-900/80 sm:rounded-md sm:p-2 sm:border sm:border-gray-200 sm:dark:border-gray-800 sm:shadow-xs xs:p-1">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 w-full sm:gap-2 xs:gap-1">
                                {/* DOLP Rewards/Lifetime Stats */}
                                <div className="bg-gray-800/40 rounded-xl p-4 border border-yellow-700 flex flex-col items-start sm:bg-yellow-100/80 sm:dark:bg-yellow-800/60 sm:rounded sm:p-2 sm:border sm:border-yellow-200 sm:dark:border-yellow-800 xs:p-1 xs:text-xs col-span-2 lg:col-span-1">
                                  <div className="flex items-center gap-3 mb-2 sm:gap-2 sm:mb-1 xs:gap-1 xs:mb-0.5">
                                    <div className="p-2 bg-yellow-500/20 rounded-lg sm:p-1 sm:rounded xs:p-0.5 xs:rounded">
                                      <FaGift className="w-4 h-4 text-yellow-400 sm:w-3 sm:h-3 xs:w-2.5 xs:h-2.5" />
                                    </div>
                                    <span className="text-sm text-yellow-400 font-bold sm:text-xs xs:text-[10px]">DOLP Rewards</span>
                                  </div>
                                  <div className="text-xs text-gray-300 mb-1">Lifetime Earned: <span className="font-bold text-yellow-300">{userStats && userStats.vested !== undefined ? `${toNumberSafe(userStats.vested).toLocaleString()} DOLP` : '-'}</span></div>
                                  <div className="text-xs text-gray-300 mb-1">Claimed: <span className="font-bold text-green-300">{userStats && userStats.totalClaimed !== undefined ? `${toNumberSafe(userStats.totalClaimed).toLocaleString()} DOLP` : '-'}</span></div>
                                  <div className="text-xs text-gray-300 mb-1">Claimable: <span className="font-bold text-cyan-300">{userStats && userStats.claimable !== undefined ? `${toNumberSafe(userStats.claimable).toLocaleString()} DOLP` : '-'}</span></div>
                                  <div className="text-xs text-gray-300">Pending: <span className="font-bold text-orange-300">{userStats && userStats.pending !== undefined ? `${toNumberSafe(userStats.pending).toLocaleString()} DOLP` : '-'}</span></div>
                                </div>
                {/* Vested Tokens */}
                <div className="bg-gray-800/40 rounded-xl p-4 border border-gray-700 flex flex-col items-start sm:bg-gray-100/80 sm:dark:bg-gray-800/60 sm:rounded sm:p-2 sm:border sm:border-gray-200 sm:dark:border-gray-800 xs:p-1 xs:text-xs">
                  <div className="flex items-center gap-3 mb-2 sm:gap-2 sm:mb-1 xs:gap-1 xs:mb-0.5">
                    <div className="p-2 bg-emerald-500/20 rounded-lg sm:p-1 sm:rounded xs:p-0.5 xs:rounded">
                      <FaGem className="w-4 h-4 text-emerald-400 sm:w-3 sm:h-3 xs:w-2.5 xs:h-2.5" />
                    </div>
                    <span className="text-sm text-gray-400 sm:text-xs sm:text-gray-500 xs:text-[10px] xs:text-gray-500">Vested Tokens</span>
                  </div>
                  <p className="text-xl font-bold text-emerald-400 sm:text-base xs:text-xs">
                    {userStats && userStats.vested !== undefined 
                      ? `${toNumberSafe(userStats.vested).toLocaleString()} DOLP`
                      : '-'
                    }
                  </p>
                </div>
                {/* Claimable Now */}
                <div className="bg-gray-800/40 rounded-xl p-4 border border-gray-700 flex flex-col items-start sm:bg-gray-100/80 sm:dark:bg-gray-800/60 sm:rounded sm:p-2 sm:border sm:border-gray-200 sm:dark:border-gray-800 xs:p-1 xs:text-xs">
                  <div className="flex items-center gap-3 mb-2 sm:gap-2 sm:mb-1 xs:gap-1 xs:mb-0.5">
                    <div className="p-2 bg-cyan-500/20 rounded-lg sm:p-1 sm:rounded xs:p-0.5 xs:rounded">
                      <FaCoins className="w-4 h-4 text-cyan-400 sm:w-3 sm:h-3 xs:w-2.5 xs:h-2.5" />
                    </div>
                    <span className="text-sm text-gray-400 sm:text-xs sm:text-gray-500 xs:text-[10px] xs:text-gray-500">Claimable Now</span>
                  </div>
                  <p className="text-xl font-bold text-cyan-400 sm:text-base xs:text-xs">
                    {stats && stats.miningEnded 
                      ? `${claimableAmount.toLocaleString(undefined, {maximumFractionDigits: 2})} DOLP`
                      : 'After mining ends'
                    }
                  </p>
                </div>
                {/* Your Level */}
                <div className="bg-gray-800/40 rounded-xl p-4 border border-gray-700 flex flex-col items-start sm:bg-gray-100/80 sm:dark:bg-gray-800/60 sm:rounded sm:p-2 sm:border sm:border-gray-200 sm:dark:border-gray-800 xs:p-1 xs:text-xs">
                  <div className="flex items-center gap-3 mb-2 sm:gap-2 sm:mb-1 xs:gap-1 xs:mb-0.5">
                    <div className="p-2 bg-purple-500/20 rounded-lg sm:p-1 sm:rounded xs:p-0.5 xs:rounded">
                      <FaHammer className="w-4 h-4 text-purple-400 sm:w-3 sm:h-3 xs:w-2.5 xs:h-2.5" />
                    </div>
                    <span className="text-sm text-gray-400 sm:text-xs sm:text-gray-500 xs:text-[10px] xs:text-gray-500">Your Level</span>
                  </div>
                  <p className="text-xl font-bold text-purple-400 sm:text-base xs:text-xs">
                    {getLevelsSafe(userStats).length > 0
                      ? `Level ${Math.max(...getLevelsSafe(userStats).map(toNumberSafe))}`
                      : 'No level unlocked'
                    }
                  </p>
                </div>
                {/* Next Level */}
                <div className="bg-gray-800/40 rounded-xl p-4 border border-gray-700 flex flex-col items-start sm:bg-gray-100/80 sm:dark:bg-gray-800/60 sm:rounded sm:p-2 sm:border sm:border-gray-200 sm:dark:border-gray-800 xs:p-1 xs:text-xs">
                  <div className="flex items-center gap-3 mb-2 sm:gap-2 sm:mb-1 xs:gap-1 xs:mb-0.5">
                    <div className="p-2 bg-emerald-500/20 rounded-lg sm:p-1 sm:rounded xs:p-0.5 xs:rounded">
                      <FaArrowUp className="w-4 h-4 text-emerald-400 sm:w-3 sm:h-3 xs:w-2.5 xs:h-2.5" />
                    </div>
                    <span className="text-sm text-gray-400 sm:text-xs sm:text-gray-500 xs:text-[10px] xs:text-gray-500">Next Level</span>
                  </div>
                  <p className="text-xl font-bold text-emerald-400 sm:text-base xs:text-xs">
                    {getLevelsSafe(userStats).length > 0 && Math.max(...getLevelsSafe(userStats).map(toNumberSafe)) < 12
                      ? `Level ${Math.max(...getLevelsSafe(userStats).map(toNumberSafe)) + 1}`
                      : getLevelsSafe(userStats).length > 0 ? 'Max level' : 'Level 1'
                    }
                  </p>
                </div>
                {/* MLM Eligibility */}
                <div className="bg-gray-800/40 rounded-xl p-4 border border-gray-700 flex flex-col items-start sm:bg-gray-100/80 sm:dark:bg-gray-800/60 sm:rounded sm:p-2 sm:border sm:border-gray-200 sm:dark:border-gray-800 xs:p-1 xs:text-xs">
                  <div className="flex items-center gap-3 mb-2 sm:gap-2 sm:mb-1 xs:gap-1 xs:mb-0.5">
                    <div className="p-2 bg-purple-500/20 rounded-lg sm:p-1 sm:rounded xs:p-0.5 xs:rounded">
                      <FaGift className="w-4 h-4 text-purple-400 sm:w-3 sm:h-3 xs:w-2.5 xs:h-2.5" />
                    </div>
                    <span className="text-sm text-gray-400 sm:text-xs sm:text-gray-500 xs:text-[10px] xs:text-gray-500">Referral Earnings</span>
                  </div>
                  <p className="text-xl font-bold text-purple-400 sm:text-base xs:text-xs">
                    {getLevelsSafe(userStats).length > 0
                      ? `Up to Level ${Math.max(...getLevelsSafe(userStats).map(toNumberSafe))}`
                      : 'Unlock level'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MINE TAB */}
        {currentTab === 'mine' && (
          <div className="animate-in fade-in bg-gradient-to-br from-gray-950 to-gray-900 min-h-screen p-4 lg:p-6 rounded-2xl">
            {stats?.miningEnded ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">⛔</div>
                <h3 className="text-2xl font-bold mb-3">Mining Has Ended</h3>
                <p className="text-gray-400 mb-6">The mining phase has concluded. You can now claim your vested tokens.</p>
                <button 
                  className="bg-gradient-to-r from-emerald-600 to-green-600 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 mx-auto"
                  onClick={() => handleSetTab('claim')}
                >
                  <FaGift /> Go to Claim
                </button>
              </div>
            ) : (
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Mining Form */}
                <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-5 border border-gray-700 shadow-xl">
                  <div className="flex items-center gap-3 mb-6">
                    <FaHammer className="w-5 h-5 text-cyan-400" />
                    <h3 className="text-lg font-semibold">Mine DOLP Tokens</h3>
                  </div>

                  {/* Info Box */}
                  <div className="bg-gray-800/40 rounded-xl p-4 mb-6 border border-gray-700">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Min Amount:</span>
                        <span className="font-medium">100 USD</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Your Level:</span>
                        <span className="font-medium text-cyan-400">
                          {userStats?.levels?.length > 0 
                            ? `Level ${Math.max(...userStats.levels.map(toNumberSafe))}` 
                            : 'No level unlocked'
                          }
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Next Level:</span>
                        <span className="font-medium text-emerald-400">
                          {userStats?.levels?.length > 0 && Math.max(...userStats.levels.map(toNumberSafe)) < 12
                            ? `Level ${Math.max(...userStats.levels.map(toNumberSafe)) + 1}`
                            : userStats?.levels?.length > 0 ? 'Max level' : 'Level 1'
                          }
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Amount Input */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Amount to Spend (USD)
                    </label>
                    <input
                      type="number"
                      value={mineAmount}
                      onChange={handleAmountChange}
                      min="100"
                      step="0.01"
                      placeholder="Enter amount (min $100)"
                      className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      inputMode="decimal"
                    />
                    <p className="text-xs text-gray-400 mt-2">
                      Payment is made in USDT (ERC20). Make sure you have enough USDT balance and have approved the contract.
                    </p>
                  </div>

                  {/* Estimated Receive */}
                  <div className="bg-gray-800/40 rounded-xl p-4 mb-6 border border-gray-700">
                    <div className="flex items-center gap-2 mb-2">
                      <FaCalculator className="w-4 h-4 text-cyan-400" />
                      <span className="text-sm text-gray-300">You will receive (estimated)</span>
                    </div>
                    <div className="text-2xl font-bold text-cyan-400">
                      {mineAmount && !isNaN(mineAmount) && parseFloat(mineAmount) > 0 && stats && stats.currentPrice
                        ? `${(parseFloat(mineAmount) / toNumberSafe(stats.currentPrice)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 6})} DOLP`
                        : '0.000000 DOLP'
                      }
                    </div>
                  </div>

                  {/* Mine Button */}
                  <button
                    onClick={handleMine}
                    disabled={txLoading || !mineAmount || parseFloat(mineAmount) < 100}
                    className={`w-full py-3 rounded-xl font-semibold text-lg transition-all ${
                      txLoading || !mineAmount || parseFloat(mineAmount) < 100
                        ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white'
                    }`}
                  >
                    {txLoading ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                        <span>Mining...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <FaHammer /> Mine Tokens
                      </div>
                    )}
                  </button>
                </div>

                {/* Price Info */}
                <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-5 border border-gray-700 shadow-xl">
                  <div className="text-center">
                    <div className="inline-block p-4 bg-cyan-500/20 rounded-xl mb-4">
                      <FaDollarSign className="w-8 h-8 text-cyan-400" />
                    </div>
                    <h4 className="text-lg font-semibold mb-2">Current Price</h4>
                    <div className="text-4xl font-bold text-cyan-400 mb-6">
                      ${stats?.currentPrice ? Number(stats.currentPrice).toFixed(2) : '0.00'}
                    </div>
                    
                    <div className="bg-gray-800/40 rounded-xl p-4">
                      <h5 className="font-medium mb-3">Mining Progress</h5>
                      <div className="h-2 bg-gray-700 rounded-full overflow-hidden mb-2">
                        <div 
                          className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full"
                          style={{ width: `${Math.min(miningProgress, 100)}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-sm text-gray-400">
                        <span>Progress</span>
                        <span>{miningProgress.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

      
        {/* CLAIM TAB */}
        {currentTab === 'claim' && (
          <div className="animate-in fade-in bg-gradient-to-br from-gray-950 to-gray-900 min-h-screen p-4 lg:p-6 rounded-2xl">
            {!stats?.miningEnded ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">⏳</div>
                <h3 className="text-2xl font-bold mb-3">Mining Still Active</h3>
                <p className="text-gray-400 mb-6">Token claiming will be available after mining ends.</p>
                <button 
                  className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 mx-auto"
                  onClick={() => handleSetTab('mine')}
                >
                  <FaHammer /> Go to Mining
                </button>
              </div>
            ) : (
              <div className="max-w-2xl mx-auto">
                {/* Claim Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {/* Total Vested */}
                  <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-5 border border-gray-700 shadow-xl">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2.5 bg-emerald-500/20 rounded-lg">
                        <FaGem className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <h4 className="text-sm text-gray-400">Total Vested</h4>
                        <p className="text-2xl font-bold text-emerald-400">
                          {Number(userStats?.vested || 0).toLocaleString()} DOLP
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Claimable Now */}
                  <div className="bg-gradient-to-br from-emerald-900/20 to-green-900/20 rounded-2xl p-5 border border-emerald-800/30 shadow-xl">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2.5 bg-emerald-500/20 rounded-lg">
                        <FaGift className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <h4 className="text-sm text-gray-300">Claimable Now</h4>
                        <p className="text-2xl font-bold text-emerald-300">
                          {claimableAmount.toLocaleString(undefined, {maximumFractionDigits: 4})} DOLP
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Claim Button */}
                <div className="text-center">
                  <button
                    onClick={handleClaim}
                    disabled={txLoading || claimableAmount === 0}
                    className={`px-8 py-4 rounded-xl font-semibold text-lg transition-all ${
                      txLoading || claimableAmount === 0
                        ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white'
                    }`}
                  >
                    {txLoading ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                        <span>Claiming...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <FaGift /> Claim Tokens
                      </div>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;